import { ONBOARDING_PAGE } from "@/constants";
import { type LoginMethod, usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { useOnboardingAnalytics } from "@/context/AnalyticsProvider/onboarding";
import { useLoginModal } from "@/context/LoginModalContext";
import { useReferralAcceptance } from "@/hooks/useReferralAcceptance";
import { inFacebookBrowser, isTikTokBrowser } from "@/utils/browser";
import { redirectGameToExternalBrowser } from "@/utils/loginredirect";
import { Heading } from "@biom3/react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { SignUpButtons } from "./SignUpButtons";

const DEFAULT_TITLE = "Immutable Play, earn from a $60k rewards pool";

export const OnboardingSignUp = ({
  title,
}: {
  title?: string;
}) => {
  const { passportClient, loginDirectly } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const { handleAcceptReferral } = useReferralAcceptance();
  const { track } = useOnboardingAnalytics();
  const referralCode = useSearchParams()?.get("ref")?.trim();

  const handlePostLogin = async () => {
    // Trigger the zk address generation by calling eth_requestAccounts.
    // The token will be refreshed and include the zk address in the claims
    // after getAccessToken is called.
    const passportProvider = await passportClient.connectEvm();
    await passportProvider.request({
      method: "eth_requestAccounts",
    });

    // Auto-accept referral if present
    if (referralCode) {
      track({
        action: Action.PRESSED,
        extras: {
          control: "AcceptReferral_AutoAccept",
          controlType: ControlType.BUTTON,
          id: "referral",
        },
      });

      const success = await handleAcceptReferral(referralCode);
      if (!success) {
        console.error("Failed to accept referral, continuing onboarding");
      }
    }
  };

  const handleLogin = async (loginMethod?: LoginMethod) => {
    // Redirect to external browser if in fb browser or tiktok browser
    if (inFacebookBrowser() || isTikTokBrowser()) {
      const redirected = redirectGameToExternalBrowser(false, ONBOARDING_PAGE, {
        ...(typeof loginMethod === "string" ? { loginMethod } : undefined),
      });
      if (redirected) return;
    }

    if (loginMethod) {
      loginDirectly(loginMethod as LoginMethod, handlePostLogin);
    } else {
      await openLoginModal();
      await handlePostLogin();
    }
  };

  return (
    <div className="bg-[#0d0d0d] flex flex-col h-full max-h-[50rem]">
      <div className="relative w-full flex-1">
        <Image
          src="/images/sign-in-bg.png"
          alt="Lens flare background"
          fill
          priority
          sizes="100dvw"
        />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2/3 max-h-full aspect-square">
          <Image
            src="/images/sign-in-avatar.png"
            alt="Character Avatar"
            fill
            objectFit="contain"
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col px-8 items-center text-center justify-evenly py-4 space-y-2">
        <Heading size={["small", "medium"]}>{title || DEFAULT_TITLE}</Heading>
        <SignUpButtons
          buttonText="Sign in to get started"
          onLogin={handleLogin}
        />
      </div>
    </div>
  );
};
